/*@flow*/
import isObject from './isObject';
import isNil from './isNil';
import isString from './isString';
import create from './create';
import getTypeName from './getTypeName';
import update from './update';
import getDefaultInterfaceName from './getDefaultInterfaceName';
import extend from './extend';
import withOptions from './withOptions';
import type { PropsT, StructType } from './types';

type OptionsT = $Shape<{
  name: any,
}>;

function getDefaultName(props: {}): string {
  return 'Struct' + getDefaultInterfaceName(props);
}

function extendStruct(mixins: any[], name?: string): StructType {
  return (extend(struct, mixins, name): any);
}

function getOptions(options: OptionsT | string | void): OptionsT {
  let o = {};

  if (isObject(options) && !isString(options)) {
    o = { ...o, ...options };
  } else if (!isNil(options)) {
    o = { ...o, name: options };
  }

  return o;
}

function struct(props: PropsT, options?: OptionsT | string | void): StructType {
  options = getOptions(options);
  let { name } = options;
  let displayName = name || getDefaultName(props);

  function Struct(value: {}, path?: string[]) {
    if (Struct.is(value)) {
      // implements idempotency
      return value;
    }

    let path1 = path || [displayName];

    if (!(this instanceof Struct)) {
      // `new` is optional
      return new Struct(value, path1);
    }

    Object.keys(props).forEach((k) => {
      let expected = props[k];
      let actual = value[k];
      this[k] = create(
        expected,
        actual,
        process.env.NODE_ENV !== 'production'
          ? path1.concat(k + ': ' + getTypeName(expected))
          : null
      );
    });

    if (process.env.NODE_ENV !== 'production') {
      Object.freeze(this);
    }

    return this;
  }

  Struct.meta = {
    kind: 'struct',
    name,
    identity: false,
    props,
  };

  Struct.displayName = displayName;

  Struct.is = (x: any): %checks => x instanceof Struct;

  Struct.update = (instance, patch) => new Struct(update<{}>(instance, patch));

  Struct.extend = function(xs, name2): StructType {
    const v = [this].concat(xs);
    return extendStruct(v, name2);
  };

  withOptions(Struct);

  return ((Struct: any): StructType);
}

struct.getOptions = getOptions;
struct.getDefaultName = getDefaultName;
struct.extend = extendStruct;
export default struct;
